"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { useRouter } from "next/navigation";

export default function AddressStartForm({ compact = false }: { compact?: boolean }) {
  const [address, setAddress] = useState("");
  const addressInputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const canStartAnalysis = Boolean(address.trim());
  const hint = canStartAnalysis
    ? "주소를 바탕으로 1차 사업성을 분석합니다."
    : "주소를 입력하면 1차 분석을 시작할 수 있어요.";

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get("address")?.toString().trim() ?? "";
    if (!value) return;
    router.push(`/leads/new?address=${encodeURIComponent(value)}&autostart=1`);
  }

  function syncAddress(value: string) {
    setAddress(value);
  }

  useEffect(() => {
    const restoreBrowserAddress = () => {
      const restoredAddress = addressInputRef.current?.value.trim();
      if (!restoredAddress) return;
      setAddress((currentAddress) => currentAddress || restoredAddress);
    };

    const initialCheck = window.setTimeout(restoreBrowserAddress, 0);
    const delayedCheck = window.setTimeout(restoreBrowserAddress, 300);
    return () => {
      window.clearTimeout(initialCheck);
      window.clearTimeout(delayedCheck);
    };
  }, []);

  return (
    <form onSubmit={submit} className={`min-w-0 w-full ${compact ? "max-w-2xl" : "max-w-[640px]"}`}>
      <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-2 shadow-[0_8px_24px_rgba(29,41,61,0.10),0_2px_6px_rgba(29,41,61,0.06)] sm:flex-row sm:items-center sm:pl-3">
        <label className="flex min-h-12 flex-1 items-center gap-3 px-2 sm:px-1">
          <MapPin className="shrink-0 text-primary" size={22} />
          <span className="sr-only">태양광 설치를 검토할 주소</span>
          <input ref={addressInputRef} name="address" value={address} onChange={(event) => syncAddress(event.currentTarget.value)} onFocus={(event) => syncAddress(event.currentTarget.value)} onBlur={(event) => syncAddress(event.currentTarget.value)} className="h-full w-full bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground" placeholder="도로명 또는 지번 주소를 입력해 주세요" autoComplete="street-address" aria-describedby="landing-analysis-hint" />
        </label>
        <button type="submit" disabled={!canStartAnalysis} className="zzaeng-primary-action min-h-12 shrink-0 px-6" aria-describedby="landing-analysis-hint landing-analysis-notice">
          동의하고 1차 분석 시작 <ArrowRight size={18} />
        </button>
      </div>
      <p id="landing-analysis-hint" role="status" className={`mt-2 flex items-start gap-1.5 text-left text-[13px] leading-5 ${canStartAnalysis ? "text-muted-foreground" : "text-[#8f4e00]"}`}>
        <span aria-hidden="true">{canStartAnalysis ? "✓" : "!"}</span>{hint}
      </p>
      <p id="landing-analysis-notice" className="mt-2 text-left text-xs leading-5 text-muted-foreground">버튼을 누르면 입력한 주소를 1차 사업성 분석에 이용하는 데 동의합니다. <a href="/privacy" className="font-semibold text-primary underline underline-offset-2">수집·이용 안내</a></p>
      <p className="mt-2 text-left text-xs text-muted-foreground">로그인 없이 1차 사업성을 확인하고, 견적을 요청할 때 가입할 수 있어요.</p>
    </form>
  );
}
